import { useContext } from 'react';

import { MessageContext } from '@/helpers/MessageProvider';
import useTelemetryData from '@/hooks/useTelemetryData';

const useMissionCommand = () => {
  const { publishCommand } = useContext(MessageContext);
  const { getAddress } = useTelemetryData();

  const clear = (missionType) => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'mission/clear', [[...address, missionType]]);
    const topics = [`${robot.id}/telemetry/missionAck`];

    // PX4는 missionAck만으로는 실패 시의 원인을 특정할 수 없음
    // statustext 메시지를 통해 상세한 실패 원인 정보를 제공함
    // 따라서 PX4는 statustext 토픽을 추가 구독하여 명령 실패에 대응함
    if (robot.model.maker.includes('PX4')) {
      topics.push(`${robot.id}/telemetry/statustext`);
    }

    return {
      execute,
      topics,
      isMatch: (data) => {
        // PX4는 statustext 토픽에서 Busy 메시지가 있으면 매칭
        if (robot.model.maker.includes('PX4') && data.text?.includes('WPM: IGN CLEAR CMD: Busy')) {
          return true;
        }

        return data.missionType === missionType;
      },
      isSuccess: (data) => {
        // isMatch에서 이미 Busy 메시지만 필터링되므로, text가 있으면 실패
        if ('text' in data) {
          return false;
        }

        return data.type === 0;
      },
    };
  };

  const requestItem = (seq, missionType) => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'mission/request_item', [[...address, seq, missionType]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/missionItem`,
      isMatch: (data) => data.seq === seq,
    };
  };

  const requestList = (missionType) => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'mission/request_list', [[...address, missionType]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/missionCount`,
      isMatch: (data) => data.missionType === missionType,
    };
  };

  const sendAck = (robot, type, missionType) => {
    const address = getAddress(robot.id);
    publishCommand(robot, 'mission/send_ack', [[...address, type, missionType]]);
  };

  const sendCount = (count, missionType) => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'mission/send_count', [[...address, count, missionType]]);

    return {
      execute,
      topics: [`${robot.id}/telemetry/missionRequest`, `${robot.id}/telemetry/missionRequestInt`],
      isMatch: (data) => data.missionType === missionType,
      isSuccess: (data) => data.seq === 0,
    };
  };

  const sendItem = (message, missionType, seq, totalCount) => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'mission/send_item', [[...address, ...message, missionType]]);

    if (seq < totalCount - 1) {
      return {
        execute,
        topics: [`${robot.id}/telemetry/missionRequest`, `${robot.id}/telemetry/missionRequestInt`],
        isSuccess: (data) => {
          // 시너플라이 기체 미션 업로드 시 seq 응답 이상 현상 대응
          if (robot.name.includes('시너플라이')) {
            return true;
          }
          return data.seq === seq + 1;
        },
      };
    }

    return {
      execute,
      topic: `${robot.id}/telemetry/missionAck`,
      isMatch: (data) => data.missionType === missionType,
      isSuccess: (data) => {
        // 시너플라이 기체 미션 업로드 시 seq 응답 이상 현상 대응
        if (robot.name.includes('시너플라이')) {
          return true;
        }
        return data.type === 0;
      },
    };
  };

  const startArduPilot = () => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'mission/start/ardupilot', [[...address]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/commandAck`,
      isMatch: (data) => data.command === 300,
      isSuccess: (data) => data.result === 0,
    };
  };

  const startPX4 = () => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'mission/start/px4', [[address[0]]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/commandAck`,
      isMatch: (data) => data.command === 176,
      isSuccess: (data) => data.result === 0,
    };
  };

  return {
    clear,
    requestItem,
    requestList,
    sendAck,
    sendCount,
    sendItem,
    startArduPilot,
    startPX4,
  };
};

export default useMissionCommand;
